import { useEffect, useState } from "react";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import { personaOptions, adPanelContent } from "@/mocks/intake-pages";
import {
  clearStage1Answers,
  getTotalIntakeSteps,
} from "@/lib/stage1Questionnaire";

export default function IntakeWelcome() {
  const { phase, goTo, containerClasses } = usePageTransition();
  const [selected, setSelected] = useState("");
  const totalSteps = getTotalIntakeSteps();

  // Landing on the welcome screen always starts a fresh intake — wipe any
  // answers left over from a previous run (dev reload, abandoned session, etc).
  useEffect(() => {
    clearStage1Answers();
    sessionStorage.removeItem("intake_persona");
    sessionStorage.removeItem("intake_transition_direction");
  }, []);

  const handleSelect = (value: string) => {
    setSelected(value);
    sessionStorage.setItem("intake_persona", value);
    setTimeout(() => {
      goTo("/intake/step/A");
    }, 500);
  };

  return (
    <div className="h-screen flex flex-col bg-background-50 relative">
      <IntakeTransitionOverlay />
      {/* Full-screen hero image background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=photorealistic%20warm%20image%20of%20a%20middle%20aged%20lower%20limb%20amputee%20without%20prosthesis%20residual%20limb%20visible%20sitting%20comfortably%20on%20a%20modern%20neutral%20toned%20sofa%20in%20a%20well%20lit%20living%20room%20looking%20intently%20at%20smartphone%20displaying%20platform%20intake%20interface%20genuine%20smile%20of%20relief%20and%20hope%20ready%20to%20begin%20journey%20soft%20diffused%20natural%20light%20cream%20beige%20palette%20hygge%20interior%20design%20peaceful%20morning%20atmosphere%20clean%20modern%20living%20space&width=1920&height=1080&seq=intake-welcome-investor-v3&orientation=landscape"
          alt="Patient beginning their prosthetic care journey from the comfort of home"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/45" />
      </div>

      {/* Content Layer */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Main Content */}
        <div
          className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 pt-20 pb-8 transition-all duration-500 ease-out ${containerClasses}`}
        >
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch">
              {/* Left: Question Panel */}
              <div className="lg:w-[55%] flex flex-col justify-center">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-10 animate-fadeSlideIn">
                  {/* Warm headline copy */}
                  <div className="mb-8">
                    <h1 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 leading-snug">
                      Your journey to comfortable mobility starts right here.
                    </h1>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl">
                      Quick Prosthetics connects lower limb amputees with
                      certified prosthetists through a simple, conversational
                      intake process. No intimidating forms, no cold phone calls
                      — just compassionate care tailored to you.
                    </p>
                  </div>

                  {/* Core Question */}
                  <div>
                    <p className="text-sm md:text-base text-foreground-600 mb-2">
                      Welcome to Quick Prosthetics
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500 mb-6">
                      We believe your care should put you first, right from the
                      comfort of home. To get started, are you completing this
                      for yourself?
                    </p>

                    <div className="space-y-3">
                      {personaOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleSelect(opt.value)}
                          className={`w-full text-left px-5 py-4 rounded-xl border-2 text-sm md:text-base font-medium transition-all cursor-pointer ${
                            selected === opt.value
                              ? "bg-primary-500 text-background-50 border-primary-500 shadow-sm"
                              : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:bg-primary-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {selected && (
                      <div className="mt-4 flex items-center gap-2 text-primary-600 animate-fadeSlideIn">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-loader-4-line text-sm animate-spin"></i>
                        </span>
                        <span className="text-xs">
                          Taking you to the next step...
                        </span>
                      </div>
                    )}

                    {!selected && (
                      <button
                        type="button"
                        onClick={() => goTo("/intake/myfit-scan")}
                        className="w-full mt-4 py-3 rounded-xl border border-background-200 bg-transparent text-foreground-500 text-xs md:text-sm font-medium hover:border-foreground-300 hover:text-foreground-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-skip-forward-line text-sm"></i>
                        </span>
                        Skip this step — I&apos;ll do it later
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Ad Panel */}
              <div className="lg:w-[45%] flex">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 md:p-8 flex flex-col w-full animate-slideUpIn">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4 self-start">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-play-circle-line text-[9px]"></i>
                    </span>
                    Contextual Video Resource
                  </span>

                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-3">
                    {adPanelContent.defaultPage1.title}
                  </h3>

                  <div className="rounded-xl overflow-hidden mb-4 flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=warm%20bright%20modern%20living%20room%20scene%20amputee%20person%20smiling%20confidently%20looking%20at%20tablet%20screen%20with%20caregiver%20nearby%20soft%20natural%20lighting%20neutral%20cream%20beige%20tones%20editorial%20lifestyle%20photography%20feeling%20of%20hope%20empowerment%20clean%20composition&width=600&height=340&seq=intake-ad-default-investor&orientation=landscape"
                      alt="Patient-first care overview"
                      className="w-full h-auto"
                    />
                  </div>

                  <p className="text-sm text-foreground-500 leading-relaxed flex-1">
                    {adPanelContent.defaultPage1.subtitle}
                  </p>

                  <div className="mt-4 pt-4 border-t border-background-100 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                        <i className="ri-vidicon-line text-sm"></i>
                      </span>
                    </span>
                    <p className="text-[11px] text-foreground-400">
                      This short video explains how our intake process works and
                      what to expect on your journey to walking comfortably.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="relative z-10 w-full max-w-[1100px] mx-auto px-4 md:px-6 pb-4">
          <IntakeProgressBar
            currentStep={1}
            totalSteps={totalSteps}
            variant="overlay"
          />
        </div>

        {/* Compliance Footer */}
        <div className="relative z-10 w-full">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6 pb-6">
            <div className="flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-background-50/50 mt-0.5">
                <i className="ri-information-line text-[10px]"></i>
              </span>
              <p className="text-[10px] text-background-50/55 leading-relaxed max-w-3xl drop-shadow-sm">
                Notice: This platform is an administrative optimization tool.
                All AI-generated functional assessments, financial forecasts,
                wholesale cost aggregations, advertising recommendations, 3D
                metric visualizations, and documentation templates are strictly
                informational. Final clinical specifications, billing
                submittals, prescriptions, and custom fabrication workflows
                require formal, independent review and explicit sign-off by a
                licensed physician and a Certified Prosthetist prior to patient
                care.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
